import { Component, lazy, Suspense, useEffect, type ReactNode } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router';
import { Shell } from './components/Shell.tsx';
import { t } from './i18n/index.ts';
import { StoreProvider, useStore } from './state/store.tsx';
import { ToastProvider } from './state/toast.tsx';
import { ConnectionsScreen, SettingsScreen } from './screens/Connections.tsx';
import { ForecastScreen } from './screens/Forecast.tsx';
import { GoalsScreen } from './screens/Goals.tsx';
import { HomeScreen } from './screens/Home.tsx';
import { PlanScreen } from './screens/Plan.tsx';
import { ShareScreen } from './screens/Share.tsx';
import { ShareCreateScreen } from './screens/ShareCreate.tsx';
import { SignInScreen } from './screens/SignIn.tsx';
import { PlansScreen } from './screens/Plans.tsx';
import { ShareConfirmScreen } from './screens/ShareConfirm.tsx';
import { EmptyPlan, ErrorState, LoadingState, NotFound, Splash } from './screens/States.tsx';
import { WhatIfScreen } from './screens/WhatIf.tsx';
import { PublicUpgradeScreen, UpgradeScreen } from './screens/Upgrade.tsx';
import { WelcomeScreen } from './screens/Welcome.tsx';
import { BillingCancelledScreen, BillingSuccessScreen } from './screens/BillingReturn.tsx';
import { UpgradeProvider } from './components/UpgradePrompt.tsx';

class ErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="state-panel" role="alert">
        <h1 className="state-panel__title">{t('error.boundary')}</h1>
        <button type="button" className="button button--primary" onClick={() => window.location.reload()}>{t('common.retry')}</button>
      </div>
    );
  }
}

/**
 * `?plan=<id>` (a chat result's link) opens that plan when this account can, else says it can't;
 * the rest of the link (version, hash) is checked by the screen's ChatBanner.
 */
function usePlanFromLink(): 'ok' | 'switching' | 'missing' {
  const { plans, activePlanId, switchPlan } = useStore();
  const [params] = useSearchParams();
  const wanted = params.get('plan');
  const known = wanted !== null && plans.some((p) => p.id === wanted);
  useEffect(() => {
    if (wanted && known && wanted !== activePlanId) void switchPlan(wanted);
  }, [wanted, known, activePlanId, switchPlan]);
  if (!wanted) return 'ok';
  if (!known) return 'missing';
  return wanted === activePlanId ? 'ok' : 'switching';
}

function PlanMissing() {
  return (
    <div className="state-panel" role="alert">
      <h1 className="state-panel__title">{t('chat.planMissing.title')}</h1>
      <p className="muted">{t('chat.planMissing.body')}</p>
      <Link to="/" className="button button--primary">{t('error.backHome')}</Link>
    </div>
  );
}

function SignedIn() {
  const { auth, planStatus } = useStore();
  const location = useLocation();
  const linked = usePlanFromLink();
  if (auth === 'loading') return <Splash />;
  if (auth !== 'signedIn') {
    if (location.pathname === '/upgrade') return <PublicUpgradeScreen />;
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={next === '/' ? '/signin' : `/signin?next=${encodeURIComponent(next)}`} replace />;
  }
  let planBody: ReactNode;
  if (linked === 'missing') planBody = <PlanMissing />;
  else if (linked === 'switching' || planStatus === 'loading' || planStatus === 'idle') planBody = <LoadingState />;
  else if (planStatus === 'error') planBody = <ErrorState />;
  else if (planStatus === 'empty') planBody = <EmptyPlan />;
  else {
    planBody = (
      <ErrorBoundary key={location.pathname}>
        <Routes>
          <Route index element={<HomeScreen />} />
          <Route path="forecast" element={<ForecastScreen />} />
          <Route path="plan" element={<PlanScreen />} />
          <Route path="what-if" element={<WhatIfScreen />} />
          <Route path="goals" element={<GoalsScreen />} />
          <Route path="share" element={<ShareCreateScreen />} />
          <Route path="connections" element={<ConnectionsScreen />} />
          <Route path="settings" element={<SettingsScreen />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ErrorBoundary>
    );
  }
  // Plans, share drafts and billing don't need the open plan's document, so they work whatever state it is in.
  return (
    <Shell>
      <ErrorBoundary key={location.pathname}>
        <Routes>
          <Route path="plans" element={<PlansScreen />} />
          <Route path="share/confirm/:id" element={<ShareConfirmScreen />} />
          <Route path="upgrade" element={<UpgradeScreen />} />
          <Route path="billing/success" element={<BillingSuccessScreen />} />
          <Route path="billing/cancelled" element={<BillingCancelledScreen />} />
          <Route path="*" element={planBody} />
        </Routes>
      </ErrorBoundary>
    </Shell>
  );
}

/** The public playground: its own chunk and its own router, so the signed-in app never loads it (or its personas). */
const TryApp = lazy(() => import('./try/TryApp.tsx'));
const isTryPath = (pathname: string) => pathname === '/try' || pathname.startsWith('/try/');
/** Start, the web onboarding: its own chunk, inside the store so it knows who is starting. */
const StartApp = lazy(() => import('./start/StartApp.tsx'));

export function App() {
  if (isTryPath(window.location.pathname)) {
    return (
      <Suspense fallback={<Splash />}>
        <TryApp />
      </Suspense>
    );
  }
  return (
    <BrowserRouter>
      <ToastProvider>
        <UpgradeProvider>
          <Routes>
            <Route path="/s/:token" element={<ShareScreen />} />
            <Route
              path="*"
              element={
                <StoreProvider>
                  <Routes>
                    <Route path="/signin" element={<SignInScreen />} />
                    <Route path="/welcome" element={<WelcomeScreen />} />
                    <Route path="/start/*" element={<Suspense fallback={<Splash />}><StartApp /></Suspense>} />
                    <Route path="/*" element={<SignedIn />} />
                  </Routes>
                </StoreProvider>
              }
            />
          </Routes>
        </UpgradeProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}
